import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as PIXI from "pixi.js";
import { ASSETS } from "../../assets";
import { initializeStyles } from "../../utils/injectStyles";
import FXManager from "../../Engine/Effects/FXManager";
import { getRulesPath } from "../../utils/pathConfig";
import { sanitizeCrowdinRulesHtml } from "../../utils/sanitizeCrowdinRulesHtml";
import { inlineSharedRules } from "../../utils/sharedRules";
import { Settings } from "../../settings";
import { FEATURES_CONFIG } from "../../featuresConfig";
import i18n from "../../i18n";
import { createStallGuard } from "../../utils/asyncTimeout";

/**
 * Textures the intro popup needs before it can render anything.
 *
 * These are force-loaded ahead of the main bundle so the intro can appear while
 * the other ~70 textures, every symbol spine and all sounds are still
 * downloading — the player reads the feature slider instead of watching a
 * loader. Same trick LoaderController already uses for preloader.json/.atlas.
 *
 * Nothing is duplicated: loadAssets' texture loop is cache-guarded
 * (`if (!PIXI.Assets.cache.has(name))`), so it simply skips whatever this
 * pre-pass already warmed.
 */
/**
 * Register an alias with the resolver at most once.
 *
 * The intro pre-pass and the main bundle cover overlapping names, and a second
 * Assets.add for the same alias makes PIXI log "[Resolver] already has key: X
 * overwriting" for every one of them. Idempotent registration keeps the console
 * clean and skips redundant work — the cache check that follows already
 * prevents a second download.
 */
const addAssetOnce = (alias, src) => {
  if (PIXI.Assets.resolver?.hasKey?.(alias)) return;
  if (PIXI.Assets.cache.has(alias)) return;
  PIXI.Assets.add({ alias, src });
};

// Cap on waiting for translations before showing the intro. Exceeding it just
// means captions fall back to their defaults — far better than never showing
// the popup at all.
const I18N_READY_MAX_WAIT_MS = 4000;

// Silence allowed inside a bundle before it is abandoned.
//
// NOT a cap on how long a bundle may take — a player on a bad connection is
// entitled to as long as they need, and any flat deadline long enough to be
// fair to them is far too long to catch a dead socket. The clock resets every
// time a single file lands, so what these actually measure is a bundle that
// has gone completely quiet: the request was accepted and then nothing came
// back, which is the one case that used to hang the boot for good.
//
// Both bundles degrade rather than fail. The intro one is already wired that
// way (loadIntroAssets.rejected sets introAssetsReady anyway, so the popup
// appears with whatever art arrived), and a rejected main bundle is caught and
// warned past in initializeGame. Missing assets are survivable here — see the
// per-asset catches below, which have always let the game run without them.
const INTRO_BUNDLE_STALL_MS = 15000;
const MAIN_BUNDLE_STALL_MS = 20000;

// What the intro popup ACTUALLY draws — nothing more.
//
// The intro gate waits on every name in this list, so anything here that the
// popup does not show delays the popup for no gain. Zeus's six split pot/coins
// images used to be here; the slides are the startpage_image_* set now, and
// those six are only paytable art, so they load with the rest of the game
// instead of holding up the first screen.
const INTRO_TEXTURE_NAMES = [
  // The background the layout is measured off, in both orientations — see
  // BG_ALIAS in IntroPopupComposedController.
  "intro_background_landscape",
  // Painted in portrait (2026-10-02) — the layout measures the landscape one.
  "intro_background_portrait",
  "logo",
  // The six slides, in order (pages 4 and 5 added 2026-10-02; page 2 has two
  // images).
  "startpage_image_1",
  "startpage_image_2",
  "startpage_image_2_1",
  "startpage_image_3",
  "startpage_image_4",
  "startpage_image_5",
  "intro_feature_superturbo",
  // Not drawn by the popup — measured. _computeLandscapeContentBottomY reads
  // this texture's native size to place the landscape content, and falls back
  // to 542x746 when it is missing. Preloaded so the real numbers are used.
  // Pagination dots and the in-popup sound button both read from this sheet.
  "ui_spritesheet_new",
];

export const loadIntroAssets = createAsyncThunk(
  "assets/loadIntroAssets",
  async (_, { rejectWithValue }) => {
    try {
      // Fonts first — the intro renders PIXI.Text (MAX WIN, slide captions).
      // Without this they would rasterise in a fallback face and never reflow.
      await initializeStyles();

      // The intro's slide captions and "TAP TO START" go through t(). i18n is
      // configured initImmediate:false with an async http backend, and the
      // intro now appears far earlier than before — without waiting here it
      // can paint raw translation keys. Resolves instantly once loaded, and a
      // failure must not block the intro, so it degrades to defaults.
      try {
        if (!i18n.isInitialized) {
          // Capped: introAssetsReady is the sole trigger for the loader
          // hand-off, so an i18n backend that never resolves would otherwise
          // pin the player on the loader even after the game fully loads.
          await new Promise((resolve) => {
            let settled = false;
            const done = () => {
              if (settled) return;
              settled = true;
              clearTimeout(timer);
              i18n.off("initialized", done);
              resolve();
            };
            const timer = setTimeout(done, I18N_READY_MAX_WAIT_MS);
            i18n.on("initialized", done);
          });
        }
      } catch {
        // Fall through — t() returns its defaultValue.
      }

      const wanted = new Set(INTRO_TEXTURE_NAMES);
      const entries = ASSETS.textures.filter((asset) => wanted.has(asset.name));

      const guard = createStallGuard(INTRO_BUNDLE_STALL_MS, "intro bundle");
      guard.start();
      try {
        await Promise.race([
          Promise.all(
            entries.map(async (asset) => {
              try {
                addAssetOnce(asset.name, asset.url);
                if (!PIXI.Assets.cache.has(asset.name)) {
                  await PIXI.Assets.load(asset.name);
                }
              } catch {
                // Match loadAssets: a missing texture must never block the rest.
                return null;
              } finally {
                // Landed or failed — either way this bundle is still moving.
                guard.progress();
              }
            }),
          ),
          guard.promise,
        ]);
      } finally {
        guard.stop();
      }

      return { loaded: entries.length };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const loadAssets = createAsyncThunk(
  "assets/loadAssets",
  async (_, { rejectWithValue }) => {
    try {
      // Inject CSS styles and preload fonts
      await initializeStyles();

      // Enable mipmaps before loading any textures — must happen before PIXI.Assets.load()
      // Pre-generates smaller texture levels so downscaled sprites render smoothly
      // ⚠️ DISABLED: Global mipmap causes black background flashes on PIXI.Text (coin values).
      // TODO: Apply selectively to UI button/icon textures only.
      // PIXI.BaseTexture.defaultOptions.mipmap = PIXI.MIPMAP_MODES.ON;

      // Wait for all web fonts to load before proceeding
      // This ensures PixiJS Text components can use custom fonts immediately
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      } else {
        // Fallback: wait a bit for fonts to start loading
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Watches the whole bundle for silence. Every branch below reports in as
      // it finishes a file, so this only ever fires when NOTHING is arriving.
      const guard = createStallGuard(MAIN_BUNDLE_STALL_MS, "asset bundle");
      guard.start();

      // 🚀 PARALLEL LOADING - Load all textures simultaneously
      const texturePromises = ASSETS.textures.map(async (asset) => {
        try {
          // Add asset with alias so it can be retrieved by name.
          // Idempotent — the intro pre-pass already registered its own subset.
          addAssetOnce(asset.name, asset.url);

          if (!PIXI.Assets.cache.has(asset.name)) {
            await PIXI.Assets.load(asset.name);
          }
        } catch (textureError) {
          // Missing asset won't block other assets - fail silently
          // Return null to indicate failure but allow other assets to load
          return null;
        } finally {
          guard.progress();
        }
      });

      // 🚀 PARALLEL LOADING - Load all spine animations simultaneously
      const spinePromises = ASSETS.spineAnimations.map(async (animation) => {
        const baseName = animation.name;
        const jsonPath = animation.json;
        // A textureless skeleton has no atlas and no page image — it is pure
        // animation data driving bones and slot colours, with every visual
        // supplied by the app (winline_text is a PIXI.Text on a bone). Asking
        // for an .atlas that does not exist 404s, and because both aliases are
        // loaded in one call, the FAILURE TAKES THE SKELETON DOWN WITH IT.
        const textureless = animation.textureless === true;
        const atlasPath = textureless
          ? null
          : animation.atlas || jsonPath.replace(".json", ".atlas");

        // Validate paths before attempting to load
        if (!jsonPath || (!textureless && !atlasPath) || !baseName) {
          throw new Error(
            `Invalid spine configuration for ${baseName}: missing path or name`
          );
        }

        // Check for malformed paths (should not contain undefined, null, HTML, or empty segments)
        const invalidPatterns = [
          "undefined",
          "null",
          "<!DOCTYPE",
          "<html",
          "DOCTYPE html",
          "window.__OPTIONS__",
          "response.ok",
          "Call the session",
        ];

        const hasInvalidPattern = invalidPatterns.some(
          (pattern) =>
            jsonPath.includes(pattern) ||
            (atlasPath && atlasPath.includes(pattern)) ||
            baseName.includes(pattern)
        );

        if (
          hasInvalidPattern ||
          jsonPath.endsWith("/.json") ||
          !jsonPath.endsWith(".json") ||
          (!textureless &&
            (atlasPath.endsWith("/.atlas") || !atlasPath.endsWith(".atlas")))
        ) {
          throw new Error(`Invalid spine path for ${baseName}: ${jsonPath}`);
        }

        // Final validation: ensure paths are valid URLs and don't contain HTML
        try {
          // Validate URL format
          const testUrl = new URL(jsonPath, window.location.origin);
          if (!testUrl.pathname.endsWith(".json")) {
            throw new Error(`Invalid JSON path format: ${jsonPath}`);
          }
        } catch (urlError) {
          // If URL construction fails, it's likely a relative path - validate manually
          if (
            !jsonPath.startsWith("/") &&
            !jsonPath.startsWith("./") &&
            !jsonPath.startsWith("http")
          ) {
            throw new Error(`Invalid path format: ${jsonPath}`);
          }
        }

        try {
          // Skip if already loaded (e.g. logo spine pre-loaded by backport Loader)
          if (!PIXI.Assets.cache.has(`${baseName}-skeleton`)) {
            PIXI.Assets.add({ alias: `${baseName}-skeleton`, src: jsonPath });
            if (textureless) {
              await PIXI.Assets.load(`${baseName}-skeleton`);
            } else {
              PIXI.Assets.add({ alias: `${baseName}-atlas`, src: atlasPath });
              await PIXI.Assets.load([
                `${baseName}-skeleton`,
                `${baseName}-atlas`,
              ]);
            }
          }
        } catch (spineError) {
          // Missing asset won't block other assets - fail silently
          // Return null to indicate failure but allow other assets to load
          return null;
        } finally {
          guard.progress();
        }
      });

      // 🚀 PARALLEL LOADING - Load all bitmap fonts simultaneously
      const fontPromises = ASSETS.bitmapFonts.map(async (font) => {
        try {
          if (!PIXI.Assets.cache.has(font.json)) {
            await PIXI.Assets.load(font.json);
          }
        } catch (fontError) {
          // Missing asset won't block other assets - fail silently
          // Return null to indicate failure but allow other assets to load
          return null;
        } finally {
          guard.progress();
        }
      });

      // 🔗 SEQUENTIAL LOADING - RevoltFX effect bundles
      // All effects share a single FX instance inside FXManager.
      // First effect uses loadBundleFiles (RevoltFX's native loader).
      // Additional effects use loadAdditionalBundle (unique PIXI aliases
      // to bypass RevoltFX's hardcoded rfx_* keys that cause cache collision).
      const effectsLoaded = (async () => {
        for (const effect of ASSETS.effects) {
          try {
            if (effect.name === "particle_trail_effect") {
              // Trail particles — needs its own FX instance (self-contained
              // bundle). Tested FIRST, before the isReady branch: this used to
              // sit second, so whenever the trail was the only effect in the
              // list it went through FXManager.init() into the MAIN instance
              // instead, leaving isTrailReady false and emitTrail returning
              // null. That is a game with pot charging off — Mad Labs.
              await FXManager.initTrailBundle(effect.bundle, effect.spritesheet);
            } else if (effect.name === "coin_trail_effect") {
              // Into the trail instance: it creates it when it is the first
              // trail bundle (Cleopatra's Fire loads no trail.json), and is
              // added beside it otherwise.
              if (!FXManager.isTrailReady) {
                await FXManager.initTrailBundle(effect.bundle, effect.spritesheet);
              } else {
                await FXManager.addTrailBundle(
                  effect.bundle,
                  effect.spritesheet,
                  effect.name,
                );
              }
            } else if (!FXManager.isReady) {
              // First effect — full init (creates FX instance + ticker)
              await FXManager.init(effect.bundle, effect.spritesheet);
            } else {
              // Additional effects — load into same FX instance
              await FXManager.loadAdditionalBundle(
                effect.bundle,
                effect.spritesheet,
                effect.name,
              );
            }
          } catch (effectError) {
            // Effect assets are optional — game continues without particle effects
          } finally {
            guard.progress();
          }
        }

        // Warm particle pools off-screen while the loading screen is still up, so
        // the first bolt/trail of the bonus doesn't pay the cold-start (empty pools).
        // Best-effort — never blocks load.
        try {
          await FXManager.warmUpPools();
        } catch (warmupError) {
          // Warmup is optional — ignore failures
        }
      })();

      // 🚀 PARALLEL LOADING - Prefetch game rules HTML
      const rulesLoaded = (async () => {
        try {
          const locale = window.__OPTIONS__?.locale || "en";
          const fallbackLng = "en";
          const MAX_WIN_MULTIPLIER = FEATURES_CONFIG.POT_COLLECTION?.MAX_WIN_MULTIPLIER || FEATURES_CONFIG.HOLD_AND_WIN?.MAX_WIN_MULTIPLIER || 10000;
          const rtpValue = window.__OPTIONS__?.math?.rtp?.main || "";

          let response = await fetch(getRulesPath(locale, fallbackLng));
          if (!response.ok) {
            response = await fetch(getRulesPath(fallbackLng, fallbackLng));
          }
          if (!response.ok) return null;

          let html = await response.text();
          // An EMPTY locale file (cleared until the Crowdin sync fills it,
          // 2026-10-12) is no rules at all — English instead.
          if (!html.trim() && locale !== fallbackLng) {
            response = await fetch(getRulesPath(fallbackLng, fallbackLng));
            if (!response.ok) return null;
            html = await response.text();
          }
          // The client's shared sections (<!-- shared:<name> --> markers),
          // from @popiplay/shared-translations — see utils/sharedRules.
          html = await inlineSharedRules(html, {
            rtp: rtpValue,
            rulesVersion: Settings.APP.RULES_VERSION,
            rulesDate: Settings.APP.RULES_DATE,
            locale, // the translated shared sections (1.0.297)
          });
          html = sanitizeCrowdinRulesHtml(html);
          html = html.replace(/{MAX_MULTIPLIER}/g, MAX_WIN_MULTIPLIER.toLocaleString());
          html = html.replace(/{RTP_VALUE}/g, rtpValue.toString());
          return html;
        } catch {
          return null;
        } finally {
          guard.progress();
        }
      })();

      // Wait for ALL assets to finish loading (happens in parallel!)
      // Note: Failed assets are handled gracefully - game continues without them
      // Failed assets return null, but we don't need to count them for production
      let allResults;
      try {
        allResults = await Promise.race([
          Promise.all([
            ...texturePromises,
            ...spinePromises,
            ...fontPromises,
            effectsLoaded,
            rulesLoaded,
          ]),
          guard.promise,
        ]);
      } finally {
        guard.stop();
      }

      // rulesLoaded is always the last promise in the array
      const rulesHtml = allResults[allResults.length - 1];


      return { rulesHtml: rulesHtml || "" };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const assetsSlice = createSlice({
  name: "assets",
  initialState: {
    status: "idle",
    error: null,
    rulesHtml: "",
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadAssets.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loadAssets.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload?.rulesHtml) {
          state.rulesHtml = action.payload.rulesHtml;
        }
      })
      .addCase(loadAssets.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "An unknown error occurred.";
      });
  },
});

export const selectRulesHtml = (state) => state.assets.rulesHtml;

export default assetsSlice.reducer;
